import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, User, Bot, Maximize2, Edit3, Info, Wrench } from 'lucide-react';

const ConversationalGrid = ({
    data,
    headers,
    currentPage,
    rowsPerPage,
    previewMode,
    pendingChanges,
    manualChanges,
    editingCell,
    editValue,
    onRowEdit,
    onCellClick,
    onEditValueChange,
    onSaveEdit,
    onCancelEdit,
    getOldValue,
    getCellValue,
    shouldUseTextarea
}) => {
    const [searchTerm, setSearchTerm] = useState('');

    // Identify conversation turn columns (user_N, assistant_N, system_N, tool_N)
    const conversationHeaderKeys = useMemo(() =>
        headers.filter(h => h !== '_rowId' && /^(user|assistant|system|tool)_\d+$/i.test(h)),
        [headers]
    );

    // Filter data based on search term
    const filteredData = useMemo(() => {
        if (!searchTerm.trim()) return data;

        const lowerSearch = searchTerm.toLowerCase();
        return data.filter(row =>
            headers.some(header => {
                const value = row[header];
                return value && String(value).toLowerCase().includes(lowerSearch);
            }) || (row._rowId && String(row._rowId).includes(lowerSearch))
        );
    }, [data, headers, searchTerm]);

    // Paginate sorted data
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const currentRows = filteredData.slice(startIndex, endIndex);

    // Group headers into turns
    const conversationTurns = useMemo(() => {
        const turns = [];
        headers.filter(h => h !== '_rowId').forEach(header => {
            const match = header.match(/^(user|assistant|system|tool)_(\d+)$/i);
            if (match) {
                const role = match[1].toLowerCase();
                const turnStr = match[2];
                turns.push({ header, role, turn: parseInt(turnStr, 10) });
            } else {
                // If it doesn't match the pattern, just treat it as a generic field at the end
                turns.push({ header, role: 'other', turn: 9999 });
            }
        });

        // Sort by turn number, then by role (system -> user -> assistant -> tool)
        turns.sort((a, b) => {
            if (a.turn !== b.turn) return a.turn - b.turn;
            const priority = { 'system': 0, 'user': 1, 'assistant': 2, 'tool': 3 };
            const pA = priority[a.role] ?? 99;
            const pB = priority[b.role] ?? 99;
            return pA - pB;
        });

        return turns;
    }, [headers]);

    // Get cell styling based on pending/manual changes
    const getBubbleStyle = (row, header) => {
        let styles = '';
        if (!previewMode) {
            const manualKey = `${row._rowId}-${header}`;
            if (manualChanges?.[manualKey]) {
                styles += 'ring-2 ring-yellow-400 border-yellow-200 ';
            }
        } else {
            const rowIdStr = row._rowId != null ? String(row._rowId) : null;
            const isDeleted = rowIdStr && (pendingChanges?.deleted || []).some(id => String(id) === rowIdStr);
            const isAdded = (pendingChanges?.added || []).some(addedRow => addedRow._rowId === row._rowId);
            const updateChange = rowIdStr && (pendingChanges?.updated || []).find(u =>
                String(u.row_id) === rowIdStr && u.changes && u.changes[header]
            );

            if (isAdded) {
                styles += 'ring-2 ring-blue-400 border-blue-200 ';
            } else if (isDeleted) {
                styles += 'line-through opacity-60 border-red-200 ring-2 ring-red-400 ';
            } else if (updateChange) {
                styles += 'ring-2 ring-emerald-400 border-emerald-200 ';
            }
        }
        return styles;
    };

    return (
        <div className="flex flex-col h-full bg-gray-50">
            {/* Search Bar */}
            <div className="px-4 py-2 bg-white border-b border-gray-200 flex items-center gap-4">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <input
                        type="text"
                        placeholder="Search conversations..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-9 pr-8 py-1.5 bg-gray-50 border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white transition-all text-sm"
                    />
                    {searchTerm && (
                        <button
                            onClick={() => setSearchTerm('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <X size={14} />
                        </button>
                    )}
                </div>
                {searchTerm && (
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                        {filteredData.length} of {data.length} conversations
                    </span>
                )}
            </div>

            {/* Conversation List */}
            <div className="flex-1 overflow-auto p-4 space-y-6">
                {currentRows.length > 0 ? (
                    <AnimatePresence mode="popLayout">
                        {currentRows.map((row, index) => (
                            <motion.div
                                key={row._rowId || index}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.2, delay: index * 0.05 }}
                                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden relative group"
                            >
                                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex justify-between items-center">
                                    <span className="text-xs font-mono text-gray-500 font-semibold bg-white px-2 py-0.5 rounded border border-gray-200 shadow-sm">
                                        Row ID: {row._rowId}
                                    </span>
                                    <button
                                        onClick={() => onRowEdit?.(row)}
                                        className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-all flex items-center gap-1 text-xs font-medium opacity-0 group-hover:opacity-100"
                                    >
                                        <Maximize2 size={14} /> Expand Editor
                                    </button>
                                </div>
                                <div className="p-4 space-y-4">
                                    {conversationTurns.map(({ header, role }) => {
                                        let cellContent = getCellValue?.(row, header) || row[header];
                                        if (typeof cellContent === 'object' && cellContent !== null) {
                                            cellContent = cellContent.content || JSON.stringify(cellContent);
                                        }
                                        if (!cellContent) return null; // Skip empty turns

                                        const isUser = role === 'user';
                                        const isSystem = role === 'system';
                                         const isTool = role === 'tool';
                                        const isEditing = editingCell?.rowId === row._rowId && editingCell?.column === header;

                                        // Tooltip logic
                                        const oldValue = getOldValue?.(row, header);
                                        const manualKey = `${row._rowId}-${header}`;
                                        const manualChange = manualChanges?.[manualKey];
                                        const showTooltip = oldValue !== null || manualChange;

                                        return (
                                            <div key={header} className={`flex w-full ${isSystem ? 'justify-center' : isUser ? 'justify-end' : 'justify-start'}`}>
                                                 <div className={`flex max-w-[80%] gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'} ${isSystem ? 'max-w-full items-center' : ''} ${isTool ? 'ml-8' : ''}`}>

                                                    {/* Avatar */}
                                                    {!isSystem && (
                                                         <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-1 ${
                                                            isUser ? 'bg-blue-100 text-blue-600' : 
                                                            isTool ? 'bg-purple-100 text-purple-600' : 
                                                            'bg-emerald-100 text-emerald-600'
                                                         }`}>
                                                             {isUser ? <User size={16} /> : isTool ? <Wrench size={14} /> : <Bot size={16} />}
                                                        </div>
                                                    )}

                                                    {/* Bubble */}
                                                    <div
                                                        className={`relative group/bubble flex flex-col ${isSystem ? 'w-full' : ''}`}
                                                    >
                                                         {isSystem && <span className="text-xs font-medium text-gray-400 mb-1 text-center">System Prompt</span>}
                                                         {isTool && <span className="text-[10px] font-bold text-purple-400 uppercase tracking-wider mb-1 ml-1">Tool Output</span>}
                                                         <div
                                                             className={`
                                                                 relative p-3 rounded-2xl text-sm shadow-sm cursor-pointer transition-colors
                                                                 ${isSystem ? 'bg-gray-100 text-gray-600 rounded-lg w-full text-center border border-gray-200'
                                                                     : isUser ? 'bg-blue-600 text-white rounded-tr-sm hover:bg-blue-700'
                                                                         : isTool ? 'bg-purple-50 text-purple-900 border border-purple-200 rounded-tl-sm hover:bg-purple-100 font-mono text-xs'
                                                                            : 'bg-white text-gray-800 border border-gray-200 rounded-tl-sm hover:bg-gray-50'}
                                                                 ${getBubbleStyle(row, header)}
                                                             `}
                                                            onClick={() => {
                                                                if (!previewMode && !isEditing) {
                                                                    onCellClick?.(row._rowId, header, cellContent);
                                                                }
                                                            }}
                                                            style={{ zIndex: isEditing ? 20 : 1 }}
                                                        >
                                                            {isEditing ? (
                                                                <div className="min-w-[300px]" onClick={e => e.stopPropagation()}>
                                                                    {shouldUseTextarea?.(editValue) ? (
                                                                        <textarea
                                                                            value={editValue}
                                                                            onChange={(e) => onEditValueChange?.(e.target.value)}
                                                                            onBlur={() => onSaveEdit?.()}
                                                                            onKeyDown={(e) => {
                                                                                if (e.key === 'Enter' && e.ctrlKey) onSaveEdit?.();
                                                                                else if (e.key === 'Escape') onCancelEdit?.();
                                                                            }}
                                                                            autoFocus
                                                                            className="w-full p-2 border-2 border-blue-400 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/50 min-h-[100px] resize-y text-gray-900"
                                                                        />
                                                                    ) : (
                                                                        <input
                                                                            type="text"
                                                                            value={editValue}
                                                                            onChange={(e) => onEditValueChange?.(e.target.value)}
                                                                            onBlur={() => onSaveEdit?.()}
                                                                            onKeyDown={(e) => {
                                                                                if (e.key === 'Enter') onSaveEdit?.();
                                                                                else if (e.key === 'Escape') onCancelEdit?.();
                                                                            }}
                                                                            autoFocus
                                                                            className="w-full p-2 border-2 border-blue-400 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-gray-900"
                                                                        />
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <div className="whitespace-pre-wrap flex items-start gap-2">
                                                                    <span>{cellContent}</span>
                                                                    {!previewMode && (
                                                                        <Edit3 size={14} className={`shrink-0 opacity-0 group-hover/bubble:opacity-50 transition-opacity mt-0.5 ${isUser ? 'text-white' : 'text-gray-400'}`} />
                                                                    )}
                                                                </div>
                                                            )}

                                                            {/* Old value tooltip (shows on hover) */}
                                                            {showTooltip && (
                                                                <div className="absolute z-50 bottom-full mb-2 hidden group-hover/bubble:block w-max max-w-xs p-2 bg-gray-900 text-white text-xs rounded-md shadow-lg pointer-events-none">
                                                                    <div className="font-semibold text-gray-300 mb-1 flex items-center gap-1">
                                                                        <Info size={12} /> {manualChange && !oldValue ? 'Manually Edited' : 'Previous Value'}
                                                                    </div>
                                                                    <div className="line-clamp-4">{oldValue || manualChange?.oldValue}</div>
                                                                    <div className="absolute top-full left-4 border-4 border-transparent border-t-gray-900"></div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </motion.div>
                        ))}
                    </AnimatePresence>
                ) : (
                    <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                        <Search size={48} className="mb-4 opacity-50" />
                        <p className="text-lg font-medium">No conversations found</p>
                        <p className="text-sm mt-2">Try adjusting your search term</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ConversationalGrid;
